/**
 * Botões com relevo ("3D"), no estilo dos botões do Intercom.
 *
 * O efeito vem de quatro camadas empilhadas em box-shadow, não de uma sombra só:
 *   1. `inset 0 1px 0` claro     → luz batendo na aresta de cima
 *   2. `inset 0 -1px 0` escuro   → a "espessura" do botão na base
 *   3. sombra externa curta      → o botão descolando da superfície
 *   4. borda 1px mais escura     → contorno que fecha o volume
 *
 * As opacidades são deliberadamente baixas: o volume deve ser percebido, não
 * notado. Se a borda ou a base chamarem atenção sozinhas, está forte demais.
 *
 * Microinteração em três estados, cada um com uma altura:
 *   repouso → hover (sobe 1px, sombra externa cresce) → active (desce 1px,
 *   espessura da base vira sombra interna = pressionado).
 */

// Ação primária (violeta). Usado no "Criar".
export const BOTAO_RELEVO_PRIMARIO = [
    'text-white font-medium',
    'bg-gradient-to-b from-violet-500 to-violet-600',
    'border border-violet-600/40',
    'shadow-[inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_0_rgba(56,18,120,0.28),0_1px_2px_rgba(109,40,217,0.18)]',
    'hover:-translate-y-px',
    'hover:from-violet-500 hover:to-violet-500',
    'hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.25),inset_0_-1px_0_rgba(56,18,120,0.28),0_2px_5px_rgba(109,40,217,0.22)]',
    'active:translate-y-px',
    'active:shadow-[inset_0_1px_2px_rgba(56,18,120,0.3)]',
    'active:from-violet-600 active:to-violet-600',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/30 focus-visible:ring-offset-1',
    'transition-all duration-200 ease-out'
].join(' ');

// Ações secundárias (ícones). Mesmo relevo, superfície neutra.
export const BOTAO_RELEVO_SECUNDARIO = [
    'bg-gradient-to-b from-white to-gray-50/60 text-gray-500',
    'border border-gray-200/70',
    'shadow-[inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(16,24,40,0.04),0_1px_2px_rgba(16,24,40,0.05)]',
    'hover:-translate-y-px hover:text-gray-800 hover:border-gray-200',
    'hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(16,24,40,0.05),0_2px_5px_rgba(16,24,40,0.08)]',
    'active:translate-y-px',
    'active:shadow-[inset_0_1px_2px_rgba(16,24,40,0.08)]',
    'active:from-gray-50 active:to-gray-50',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/25 focus-visible:ring-offset-1',
    'transition-all duration-200 ease-out',
    'dark:from-neutral-800 dark:to-neutral-800/90 dark:text-neutral-400 dark:border-neutral-700/70',
    'dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),inset_0_-1px_0_rgba(0,0,0,0.25),0_1px_2px_rgba(0,0,0,0.25)]',
    'dark:hover:text-white dark:hover:border-neutral-600',
    'dark:active:shadow-[inset_0_1px_2px_rgba(0,0,0,0.35)]'
].join(' ');

// Variante do secundário quando o botão está ativo/aplicado (ex.: filtro ligado).
export const BOTAO_RELEVO_SECUNDARIO_ATIVO = [
    'bg-gradient-to-b from-violet-50 to-violet-50/70 text-violet-600',
    'border border-violet-200/80',
    'shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(109,40,217,0.08),0_1px_2px_rgba(109,40,217,0.06)]',
    'hover:-translate-y-px hover:border-violet-300',
    'hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(109,40,217,0.1),0_2px_5px_rgba(109,40,217,0.1)]',
    'active:translate-y-px',
    'active:shadow-[inset_0_1px_2px_rgba(109,40,217,0.12)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/25 focus-visible:ring-offset-1',
    'transition-all duration-200 ease-out',
    'dark:from-violet-500/15 dark:to-violet-500/10 dark:text-violet-300 dark:border-violet-500/30'
].join(' ');

// Ação destrutiva (excluir divisão etc.). Mesmo relevo do primário, em vermelho.
export const BOTAO_RELEVO_PERIGO = [
    'text-white font-medium',
    'bg-gradient-to-b from-red-500 to-red-600',
    'border border-red-700/40',
    'shadow-[inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-1px_0_rgba(80,10,10,0.28),0_1px_2px_rgba(153,27,27,0.15)]',
    'hover:-translate-y-px',
    'hover:from-red-500 hover:to-red-500',
    'hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_0_rgba(80,10,10,0.28),0_2px_5px_rgba(153,27,27,0.2)]',
    'active:translate-y-px',
    'active:shadow-[inset_0_1px_2px_rgba(80,10,10,0.32)]',
    'active:from-red-600 active:to-red-600',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 focus-visible:ring-offset-1',
    'transition-all duration-200 ease-out'
].join(' ');

// Contraparte dos botões em relevo: superfície "afundada" para inputs na mesma
// linha. Sem isso o campo flat ao lado de botões com volume parece desabilitado.
export const INPUT_AFUNDADO = [
    'bg-gray-50/60 border-gray-200/70 rounded-lg',
    'shadow-[inset_0_1px_1px_rgba(16,24,40,0.04)]',
    'focus:bg-white focus:border-violet-300',
    'focus:shadow-[0_0_0_3px_rgba(124,58,237,0.08)]',
    'transition-all duration-200',
    'dark:bg-neutral-800/60 dark:border-neutral-700/70',
    'dark:shadow-[inset_0_1px_1px_rgba(0,0,0,0.25)]',
    'dark:focus:bg-neutral-800'
].join(' ');
